import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../../components/Header";
import { formatNum, fmtMSS, get_thumbnail_ext } from "../../util";
import { SubtitleSearchResponse, SubtitleVideoHit, SubtitleMatch } from "../../models";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.vlivearchive.com";
const PAGE_SIZE = 20;

// The corpus is subtitled in ~30 locales; these are the ones with enough
// coverage to be worth offering as a filter.
const LANGUAGES: { code: string; label: string }[] = [
  { code: "", label: "Any language" },
  { code: "en_US", label: "English" },
  { code: "ko_KR", label: "Korean" },
  { code: "ja_JP", label: "Japanese" },
  { code: "es_ES", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (BR)" },
  { code: "in_ID", label: "Indonesian" },
  { code: "zh_TW", label: "Chinese (Traditional)" },
  { code: "zh_CN", label: "Chinese (Simplified)" },
  { code: "vi_VN", label: "Vietnamese" },
  { code: "th_TH", label: "Thai" },
  { code: "tr_TR", label: "Turkish" },
  { code: "fr_FR", label: "French" },
  { code: "ru_RU", label: "Russian" },
  { code: "ar_AE", label: "Arabic" },
  { code: "it_IT", label: "Italian" },
  { code: "de_DE", label: "German" },
];

const SOURCES: { code: string; label: string }[] = [
  { code: "", label: "Any source" },
  { code: "cp", label: "Official" },
  { code: "fan", label: "Fan-made" },
  { code: "auto", label: "Auto-generated" },
];

/**
 * The API marks matched terms with <em> tags. Rather than trusting that string
 * as HTML, split on the tags and build real nodes - subtitle text is arbitrary
 * archived content and should never reach dangerouslySetInnerHTML.
 */
function Highlighted({ match }: { match: SubtitleMatch }) {
  const source = match.highlight || match.text;
  const pieces = source.split(/<em>|<\/em>/);
  return (
    <>
      {pieces.map((piece, i) =>
        // split() on a matched pair puts highlighted runs at every odd index.
        i % 2 === 1 ? (
          <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">
            {piece}
          </mark>
        ) : (
          <span key={i}>{piece}</span>
        )
      )}
    </>
  );
}

function SourceBadge({ match }: { match: SubtitleMatch }) {
  const source = (match.source || "").toLowerCase();
  const styles: { [key: string]: string } = {
    cp: "bg-blue-100 text-blue-800",
    fan: "bg-purple-100 text-purple-800",
    auto: "bg-gray-100 text-gray-600",
  };
  const labels: { [key: string]: string } = {
    cp: "official",
    fan: "fan",
    auto: "auto",
  };
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
        styles[source] || "bg-gray-100 text-gray-600"
      }`}
      title={match.translator ? `by ${match.translator}` : undefined}
    >
      {labels[source] || source || "?"}
      {match.lang ? ` · ${match.lang}` : ""}
    </span>
  );
}

function ResultCard({ hit }: { hit: SubtitleVideoHit }) {
  // thumb is null for the handful of videos with no Mongo metadata.
  const ext = get_thumbnail_ext(hit.thumb || "") || ".jpg";
  const thumbnail = `https://thumbs-sm.vlivearchive.com/${hit.videoSeq}${ext}`;
  // The first match is the strongest one, so deep-link the card at it.
  const firstMatch = hit.matches[0];
  const videoHref = `/video/${hit.videoSeq}${
    firstMatch ? `?t=${Math.floor(firstMatch.startMs / 1000)}` : ""
  }`;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-4">
      <div className="flex gap-4">
        <Link to={videoHref} className="shrink-0">
          <div className="relative w-40 h-24 sm:w-48 sm:h-28 bg-gray-100 rounded overflow-hidden">
            <img
              src={thumbnail}
              alt={hit.title || `Video ${hit.videoSeq}`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                (e.target as HTMLImageElement).style.visibility = "hidden";
              }}
            />
            {hit.playTime ? (
              <span className="absolute bottom-1 right-1 bg-black/75 text-white text-xs px-1 rounded">
                {fmtMSS(hit.playTime)}
              </span>
            ) : null}
          </div>
        </Link>

        <div className="min-w-0 flex-1">
          <Link to={videoHref}>
            <h3 className="font-semibold text-gray-900 hover:text-blue-600 line-clamp-2">
              {hit.title || `Video ${hit.videoSeq}`}
            </h3>
          </Link>
          <div className="text-sm text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
            {hit.channelName ? (
              <Link
                to={`/channel/${hit.channelCode}`}
                className="hover:text-blue-600"
              >
                {hit.channelName}
              </Link>
            ) : null}
            {hit.playCount ? <span>{formatNum(hit.playCount)} views</span> : null}
            <span>
              {hit.cueMatches} matching {hit.cueMatches === 1 ? "line" : "lines"}
            </span>
          </div>

          <ul className="mt-2 space-y-1">
            {hit.matches.map((match, i) => (
              <li key={i} className="text-sm flex gap-2 items-baseline">
                <Link
                  to={`/video/${hit.videoSeq}?t=${Math.floor(match.startMs / 1000)}`}
                  className="font-mono text-xs text-blue-600 hover:underline shrink-0 pt-0.5"
                >
                  {match.timecode}
                </Link>
                <span className="text-gray-800 break-words">
                  <Highlighted match={match} />
                </span>
                <SourceBadge match={match} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function SubtitleSearch() {
  const [params, setParams] = useSearchParams();

  const query = params.get("q") || "";
  const lang = params.get("lang") || "";
  const source = params.get("source") || "";
  const phrase = params.get("phrase") === "1";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  // Kept separate from the URL so typing does not fire a request per keystroke.
  const [draft, setDraft] = useState(query);
  const [data, setData] = useState<SubtitleSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  document.title = query
    ? `${query} — Subtitle search — KLIVE Archive`
    : "Subtitle search — KLIVE Archive";

  useEffect(() => {
    setDraft(query);
  }, [query]);

  useEffect(() => {
    if (!query) {
      setData(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE}/subtitles/search`, {
        params: {
          q: query,
          lang: lang || undefined,
          source: source || undefined,
          phrase: phrase ? 1 : undefined,
          page,
          size: PAGE_SIZE,
        },
        signal: controller.signal,
      })
      .then((response) => {
        setData(response.data);
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setData(null);
        setLoading(false);
        setError(
          err.response?.status === 503
            ? "Subtitle search is temporarily unavailable."
            : err.response?.data?.error || "Something went wrong with that search."
        );
      });
    return () => controller.abort();
  }, [query, lang, source, phrase, page]);

  /** Write the controls back into the URL so a search is shareable. */
  const update = useCallback(
    (changes: { [key: string]: string }, resetPage = true) => {
      const next = new URLSearchParams(params);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (resetPage) next.delete("page");
      setParams(next);
    },
    [params, setParams]
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    update({ q: draft.trim() });
  };

  const totalPages = data ? Math.ceil(data.totalVideos / PAGE_SIZE) : 0;
  // The API refuses windows past 10k results, so cap paging short of that.
  const maxPage = Math.min(totalPages, Math.floor(10000 / PAGE_SIZE));

  return (
    <main className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <h1 className="text-2xl font-bold text-gray-900">Subtitle search</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Search what was actually said across every archived video, rather than
          just titles.
        </p>

        <form onSubmit={submit} className="mt-4">
          <div className="flex gap-2">
            <input
              type="search"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={'e.g. "thank you so much" or 사랑해'}
              className="flex-1 p-3 text-sm text-gray-900 border border-solid border-gray-300 rounded-lg bg-white focus:ring-blue-400 focus:border-blue-500"
              autoFocus
            />
            <button
              type="submit"
              className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
            >
              Search
            </button>
          </div>

          <div className="flex flex-wrap gap-3 mt-3 items-center text-sm">
            <select
              value={lang}
              onChange={(e) => update({ lang: e.target.value })}
              className="p-2 border border-solid border-gray-300 rounded-lg bg-white text-gray-700"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>

            <select
              value={source}
              onChange={(e) => update({ source: e.target.value })}
              className="p-2 border border-solid border-gray-300 rounded-lg bg-white text-gray-700"
            >
              {SOURCES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>

            <label className="flex items-center gap-2 text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={phrase}
                onChange={(e) => update({ phrase: e.target.checked ? "1" : "" })}
                className="rounded border-gray-300"
              />
              Exact phrase
            </label>
          </div>
        </form>

        <div className="mt-6">
          {!query ? (
            <p className="text-gray-500">
              Type something above to search {""}
              <strong>147 million</strong> subtitle lines.
            </p>
          ) : loading ? (
            <p className="text-gray-500">Searching…</p>
          ) : error ? (
            <p className="text-red-600">{error}</p>
          ) : data && data.results.length === 0 ? (
            <p className="text-gray-500">
              No videos matched <strong>{query}</strong>. Try turning off
              “Exact phrase”, or widening the language filter.
            </p>
          ) : data ? (
            <>
              <p className="text-sm text-gray-500 mb-3">
                {formatNum(data.totalVideos)} video
                {data.totalVideos === 1 ? "" : "s"} ·{" "}
                {formatNum(data.totalCues)} matching line
                {data.totalCues === 1 ? "" : "s"} · {data.tookMs}ms
              </p>

              <div className="space-y-3">
                {data.results.map((hit) => (
                  <ResultCard hit={hit} key={hit.videoSeq} />
                ))}
              </div>

              {maxPage > 1 ? (
                <div className="flex items-center justify-center gap-3 mt-6">
                  <button
                    onClick={() => update({ page: String(page - 1) }, false)}
                    disabled={page <= 1}
                    className="px-4 py-2 text-sm border border-solid border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {maxPage}
                  </span>
                  <button
                    onClick={() => update({ page: String(page + 1) }, false)}
                    disabled={page >= maxPage}
                    className="px-4 py-2 text-sm border border-solid border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
}
